import type { ReactNode } from 'react'
import type { IceReaction } from '../../lib/reactions'

const LINE = 'var(--skater-line)'
const SKY = 'var(--ice-arrow)'
const ICE = 'var(--ice-surface)'
const BRASS = 'var(--ice-brass)'

/** Reaction stickers for Ice Ice Baby, drawn in the skater's flat style (0..40 box). */
const STICKERS: Record<IceReaction, ReactNode> = {
  snow: (
    <>
      <g stroke={SKY} strokeWidth="2.6" strokeLinecap="round">
        <path d="M20 5 V35 M7 12.5 L33 27.5 M7 27.5 L33 12.5" />
        <path d="M20 9 L16 12 M20 9 L24 12 M20 31 L16 28 M20 31 L24 28" strokeWidth="2" />
      </g>
      <circle cx="20" cy="20" r="3" fill={ICE} stroke={SKY} strokeWidth="2" />
      <g fill={SKY}>
        <circle cx="5" cy="6" r="1.6" />
        <circle cx="35" cy="7" r="1.3" />
        <circle cx="34" cy="34" r="1.6" />
        <circle cx="6" cy="33" r="1.2" />
      </g>
    </>
  ),
  chill: (
    <>
      <rect x="6" y="8" width="28" height="26" rx="5" fill={ICE} stroke={SKY} strokeWidth="2.2" />
      <path
        d="M10 12 L14 8.5 M10 18 L19.5 8.5"
        stroke="var(--ice-sheen)"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <path
        d="M9 19 H31 M12 19 L13.5 25 H19 L20 19 M28 19 L26.5 25 H21 L20 19"
        fill={LINE}
        stroke={LINE}
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path
        d="M15 29.5 Q21 32.5 26 28.5"
        fill="none"
        stroke={LINE}
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </>
  ),
  roar: (
    <>
      <path
        d="M8 22 C7 12 12 6 20 6 C28 6 33 12 32 22 C31.5 30 27 34 20 34 C13 34 8.5 30 8 22 Z"
        fill="var(--yeti-fur)"
        stroke="var(--yeti-fur-shade)"
        strokeWidth="1.4"
      />
      <path d="M11 8 L14 3 L16 7 L20 2.5 L24 7 L26 3 L29 8" fill="var(--yeti-fur)" />
      <path d="M9 12 L4 6 L11 9.5 Z M31 12 L36 6 L29 9.5 Z" fill="var(--ice-rock-shade)" />
      <ellipse cx="20" cy="19" rx="9" ry="8" fill="var(--yeti-face)" />
      <path d="M13 14 L17 16 M27 14 L23 16" stroke={LINE} strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="16" cy="17.5" r="1.5" fill={LINE} />
      <circle cx="24" cy="17.5" r="1.5" fill={LINE} />
      <path d="M14 22 Q20 21 26 22 Q25 30 20 30 Q15 30 14 22 Z" fill={LINE} />
      <path d="M15.5 22.5 L17 26 L18.5 22.6 M24.5 22.5 L23 26 L21.5 22.6" fill="var(--yeti-fur)" />
      <g stroke={SKY} strokeWidth="1.6" strokeLinecap="round">
        <path d="M2 18 H5 M2 23 H6 M35 18 H38 M34 23 H38" />
      </g>
    </>
  ),
  gg: (
    <>
      <path
        d="M20 3 L34 8 V19 C34 28 28 34 20 37 C12 34 6 28 6 19 V8 Z"
        fill={BRASS}
        stroke="var(--ice-wood-dark)"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path
        d="M20 6 L31 10 V19 C31 26 26.5 31 20 33.6 C13.5 31 9 26 9 19 V10 Z"
        fill="none"
        stroke="var(--ice-sheen)"
        strokeWidth="1"
        opacity=".7"
      />
      <text
        x="20"
        y="25"
        textAnchor="middle"
        fontSize="14"
        fontWeight="900"
        fontFamily="inherit"
        fill="var(--ice-wood-dark)"
      >
        GG
      </text>
    </>
  ),
  flag: (
    <>
      <rect x="7" y="4" width="2.6" height="33" rx="1.3" fill="var(--ice-wood-dark)" />
      <path
        d="M9.5 6 Q19 3 24 7 Q29 11 34 8 V22 Q29 25 24 21 Q19 17 9.5 20 Z"
        fill="white"
        stroke={LINE}
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      <g fill={LINE}>
        <path d="M9.5 6 Q14 4.5 17.5 5 V10 Q14 9.5 9.5 11 Z" />
        <path d="M17.5 10 Q21 10.5 25.5 12.5 V17 Q21 15 17.5 15 Z" />
        <path d="M25.5 7.5 Q30 10 34 8 V12.5 Q30 14.5 25.5 12.5 Z" />
        <path d="M9.5 15.5 Q14 14 17.5 15 V20 Q14 19 9.5 20 Z" />
        <path d="M25.5 17 Q30 19.5 34 17.5 V22 Q30 25 25.5 21 Z" />
      </g>
    </>
  ),
  splash: (
    <>
      <path
        d="M20 5 C26 12 33 17 33 25 C33 31.5 27.5 36 20 36 C12.5 36 7 31.5 7 25 C7 17 14 12 20 5 Z"
        fill={SKY}
      />
      <path
        d="M12 24 Q12.5 30 17 32"
        fill="none"
        stroke="var(--ice-sheen)"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <g fill={SKY}>
        <circle cx="5" cy="14" r="2" />
        <circle cx="35" cy="12" r="1.6" />
        <circle cx="4" cy="24" r="1.3" />
        <circle cx="36" cy="22" r="1.4" />
      </g>
      <text
        x="20"
        y="28"
        textAnchor="middle"
        fontSize="9.5"
        fontWeight="900"
        fontFamily="inherit"
        fill="white"
      >
        NO!
      </text>
    </>
  ),
}

export function Sticker({ id, className = '' }: { id: IceReaction; className?: string }) {
  return (
    <svg viewBox="0 0 40 40" className={className} aria-hidden>
      {STICKERS[id]}
    </svg>
  )
}
